// 这个保留，后期可能需要单独添加地区的选择器
// 如果是这样就将RegionAndLanguageSelect组件中的region组件抽离出来

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LanguageType } from '../../types';
import { commonBackInfo } from '../../utils';
import { languageTitleLocal } from '../../constants/regionAndLanguage';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import useDefaultTwoLevelFilter from '../../hooks/useDefaultTwoLevelFilter';
import { useIntl } from 'umi';
import { uniqueArray } from '@op/utils';
import { getPlaceholderText } from '../../constants/placeholder';
import { addFilterBuriedPoint } from '../../utils/point';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import { LocaleType } from '@op/types/common';
import { useOptionsMemo } from '../../hooks/common';

const chinaRegion = ['chn', 'hmt'];

export default function LanguageSelect({
  changeOnCloseWindow,
  onChange,
  defaultValue,
  filterValue,
  lang,
  filterOptions,
  oriDefaultFilter,
  disabled,
  dealwithOptions,
  showShortcut = true,
}: LanguageType) {
  const [languageOptions, setLanguageOptions] = useState<Array<any>>([]);
  const languageTransMap = useRef<{
    [key: string]: { [key in LocaleType]: string };
  }>({});
  const { formatMessage } = useIntl();
  function getAllLanguageValue(
    originalOptions: Array<{ children: any[]; value: string }>,
  ) {
    const regions: Array<string> = [];
    const markets = (originalOptions || []).reduce(
      (pre: any[], cur: { children: any[]; value: string }) => {
        regions.push(cur.value);
        pre.push(...cur.children.map((item: { value: any }) => item.value));
        return pre;
      },
      [],
    );

    return {
      regions: uniqueArray(regions),
      markets: uniqueArray(markets),
    };
  }
  useEffect(() => {
    let region: Array<any> = [];
    if (filterOptions) {
      const localFilterOptions =
        filterOptions.region_language || filterOptions || [];

      const list =
        localFilterOptions?.map?.(
          ({ region_abbre, region_name_cn, region_name_en, language }: any) => {
            return {
              value: region_abbre?.toLocaleLowerCase?.(),
              text: region_name_en,
              textCN: region_name_cn,
              children: (language || [])?.map?.(
                ({ language_en_abbre, language_cn, language_en }: any) => {
                  const key = language_en_abbre?.toLocaleLowerCase?.();

                  languageTransMap.current[key] = {
                    'zh-CN': language_cn,
                    'en-US': language_en,
                  };

                  return {
                    value: key,
                    text: language_en,
                    textCN: language_cn,
                  };
                },
              ),
            };
          },
        ) || [];
      region = list;
    }
    setLanguageOptions(region);
  }, [filterOptions]);

  const realOptions = useOptionsMemo({
    options: languageOptions,
    dealwithOptions,
    key: 'language',
    deeps: [languageOptions],
  });

  const {
    setSelectValue,
    selectValue,
    onChange: onLanguageChange,
    diffClassName,
  } = useDefaultTwoLevelFilter({
    originalOptions: realOptions,
    defaultValue,
    filterValue,
    onlySecondLevelValue: true,
    oriDefaultFilter,
    dealwithDefaultValue: (value: any) => {
      const language = value?.language || [];
      return { value: language, type: 'market' };
    },
    dealwithAllValue: (originalOptions: any) => {
      const { markets, regions } = getAllLanguageValue(originalOptions);

      return {
        markets,
        regions,
        type: 'market',
      };
    },
  });

  const languageChange = (tags: any) => {
    setSelectValue(tags);
  };

  const getRegionButtonMemo = useCallback(
    (realSelectValue = selectValue) => {
      let { value } = onLanguageChange(realSelectValue);
      value = uniqueArray(value);
      // 注意点: 这里用languageOptions,保证过滤后不会全选
      const { markets } = getAllLanguageValue(languageOptions);
      if (markets.length === value.length) {
        return {
          text: formatMessage({ id: 'All' }),
          isAll: true,
          value,
        };
      }
      let text: string | number = value.length;
      if (value.length === 0) {
        text = formatMessage({ id: 'None' });
      } else if (text <= 1) {
        text = value
          .map((item) => languageTransMap.current[item]?.[lang])
          .join(',');
      }

      return {
        text,
        isAll: false,
        value,
      };
    },
    [selectValue, formatMessage],
  );

  function getCommonBackInfo() {
    const { value, text, isAll } = getRegionButtonMemo();

    return commonBackInfo(
      { language: value },
      { language: text },
      { isAll, needChangeEntity: true },
    );
  }

  useEffect(() => {
    if (!disabled) {
      onChange?.('language', getCommonBackInfo());
    }
  }, [getRegionButtonMemo]);

  useEffect(() => {
    const data = getCommonBackInfo();
    changeOnCloseWindow?.(data.value);
  }, [selectValue]);

  const shortcutOptions = useMemo(() => {
    if (!showShortcut) return [];
    return [
      {
        textCN: '全部',
        text: 'All',
        type: 'All',
        filter: (options: any[]) => {
          const { markets } = getAllLanguageValue(options);
          return markets;
        },
      },
      {
        textCN: '非中国',
        text: 'non-Chinese',
        type: 'non-Chinese',
        filter: (options: any) => {
          const { markets } = getAllLanguageValue(
            options.filter(
              (item: { value: string }) => !chinaRegion.includes(item.value),
            ),
          );
          return markets;
        },
      },
      {
        textCN: '中国',
        text: 'Chinese',
        type: 'Chinese',
        filter: (options: any) => {
          const { markets } = getAllLanguageValue(
            options.filter((item: { value: string }) =>
              chinaRegion.includes(item.value),
            ),
          );
          return markets;
        },
      },
    ];
  }, []);

  const regionButtonElement = useMemo(() => {
    const { text } = getRegionButtonMemo();
    const placeholderText = getPlaceholderText('language');
    return (
      <>
        <span className="select-button-text">{placeholderText}</span>
        <span className="select-button-value">{text}</span>
      </>
    );
  }, [getRegionButtonMemo]);

  return (
    <div className={`language-select-wrapper ${diffClassName}`}>
      <NewRegionSelect
        value={selectValue}
        options={realOptions}
        locale={lang}
        disabled={disabled}
        button={regionButtonElement}
        shortcutOptions={shortcutOptions}
        language={languageTitleLocal}
        // radioOptions={languageRadioOptions}
        closeOnScroll={true}
        radioOptionsTips={{
          global: lang === 'en-US' ? 'No result found' : '无匹配项',
        }}
        onCloseReginSelect={(value: any) => {
          languageChange(value);

          try {
            const { value: selectValue, isAll } = getRegionButtonMemo(value);
            addFilterBuriedPoint(
              FilterButtonPoint.language,
              isAll ? 'All' : selectValue,
            );
          } catch (error) {}
        }}
      />
    </div>
  );
}
